import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Swal from 'sweetalert2';
import { 
  FaDatabase, FaTrashAlt, FaCalendarAlt, 
  FaBuilding, FaTags, FaClock, FaExclamationCircle, FaInfoCircle
} from 'react-icons/fa';
import cpApi from '../../utils/cpApi';
import './DataManager.css';

const DataManager = () => {
  const [colleges, setColleges]   = useState([]);
  const [college, setCollege]     = useState('');
  const [stats, setStats]         = useState(null);
  
  // Filters
  const [timeMode, setTimeMode]       = useState('all'); // 'all', 'year', 'custom'
  const [selYear, setSelYear]         = useState('');
  const [fromDate, setFromDate]       = useState('');
  const [toDate, setToDate]           = useState('');
  const [selCategory, setSelCategory] = useState(''); // '' = all categories
  
  const [loading, setLoading]         = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);

  // 1. Fetch Colleges
  useEffect(() => {
    cpApi.get('/controlpanel/colleges')
      .then(r => setColleges(r.data.data || []))
      .catch(() => {});
  }, []);

  // 2. Load Stats when College Changes
  const loadStats = useCallback(async (collegeId) => {
    setStatsLoading(true);
    setStats(null);
    setTimeMode('all');
    setSelYear('');
    setSelCategory('');
    setFromDate('');
    setToDate(new Date().toISOString().split('T')[0]); // Default to today

    try {
      const params = collegeId ? { college: collegeId } : {};
      const { data } = await cpApi.get('/controlpanel/data/alert-log-stats', { params });
      
      const s = data.data;
      setStats(s);
      
      if (s.earliest) {
        setFromDate(new Date(s.earliest).toISOString().split('T')[0]);
      }
      if (s.years && s.years.length > 0) {
        setSelYear(String(s.years[0].year)); // Default to most recent year
      }
    } catch { setStats(null); }
    finally { setStatsLoading(false); }
  }, []);

  const handleCollegeChange = (e) => {
    const val = e.target.value;
    setCollege(val);
    loadStats(val);
  };

  // 3. Dynamic Counts Calculation based on Time Mode
  const currentCounts = useMemo(() => {
    if (!stats) return { total: 0, categories: [], isExact: true };

    let total = 0;
    let categories = [];
    let isExact = true;

    if (timeMode === 'all') {
      total = stats.total;
      categories = stats.categories.map(c => ({ name: c.category, count: c.total }));
    } 
    else if (timeMode === 'year') {
      total = stats.years.find(y => String(y.year) === selYear)?.count || 0;
      categories = stats.categories.map(c => {
        const yCount = c.years.find(y => String(y.year) === selYear)?.count || 0;
        return { name: c.category, count: yCount };
      });
    } 
    else if (timeMode === 'custom') {
      const fromYr = fromDate ? new Date(fromDate).getFullYear() : null;
      const toYr   = toDate ? new Date(toDate).getFullYear() : null;

      // Check if dates perfectly align with start/end of years
      const isFromStart = fromDate && fromDate.endsWith('-01-01');
      const isToEnd     = toDate && toDate.endsWith('-12-31');
      if (!isFromStart || !isToEnd) isExact = false;

      total = stats.years
        .filter(y => (!fromYr || y.year >= fromYr) && (!toYr || y.year <= toYr))
        .reduce((sum, y) => sum + y.count, 0);

      categories = stats.categories.map(c => {
        const cTotal = c.years
          .filter(y => (!fromYr || y.year >= fromYr) && (!toYr || y.year <= toYr))
          .reduce((sum, y) => sum + y.count, 0);
        return { name: c.category, count: cTotal };
      });
    }

    return { total, categories, isExact };
  }, [stats, timeMode, selYear, fromDate, toDate]);

  // Selected Count (Total or specific category)
  const finalDeleteCount = selCategory 
    ? (currentCounts.categories.find(c => c.name === selCategory)?.count || 0)
    : currentCounts.total;

  // 4. Handle Deletion
  const handleDelete = async () => {
    if (!college) return Swal.fire('Select College', 'Please select a college first.', 'warning');
    if (finalDeleteCount === 0) return Swal.fire('No Logs', 'No alert logs found for these filters.', 'info');

    const colName = colleges.find(c => c._id === college)?.name;

    let timeText = 'All Time';
    if (timeMode === 'year') timeText = `Year: ${selYear}`;
    if (timeMode === 'custom') timeText = `From ${fromDate} to ${toDate || 'Now'}`;

    const warningHtml = `
      <div style="text-align:left; background:#f8fafc; padding:16px; border-radius:8px; border:1px solid #e2e8f0; margin-bottom:14px;">
        <div style="margin-bottom:6px;"><b>College:</b> ${colName}</div>
        <div style="margin-bottom:6px;"><b>Time Range:</b> ${timeText}</div>
        <div><b>Category:</b> ${selCategory ? selCategory.toUpperCase() : 'ALL CATEGORIES'}</div>
      </div>
      ${currentCounts.isExact 
        ? `<strong style="color:#e74c3c; font-size:1.1rem;">Exactly ${finalDeleteCount} log(s) will be permanently deleted.</strong>`
        : `<strong style="color:#e74c3c; font-size:1.1rem;">Logs within this date range will be permanently deleted.</strong><br/><span style="font-size:0.85rem; color:#64748b;">(Estimated maximum: ${finalDeleteCount} logs affected)</span>`
      }
    `;

    const { isConfirmed } = await Swal.fire({
      title: 'Confirm Permanent Deletion',
      html: warningHtml,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e74c3c',
      cancelButtonColor: '#94a3b8',
      confirmButtonText: 'Yes, Purge Data',
    });

    if (!isConfirmed) return;

    setLoading(true);
    try {
      const params = { college };
      if (selCategory) params.category = selCategory;
      if (timeMode === 'year') {
        params.from = `${selYear}-01-01`;
        params.to = `${selYear}-12-31`;
      } else if (timeMode === 'custom') {
        if (fromDate) params.from = fromDate;
        if (toDate) params.to = toDate;
      }
      
      const { data } = await cpApi.delete('/controlpanel/data/alert-logs', { params });
      Swal.fire('Purged!', `Successfully deleted ${data.deletedCount} alert log(s).`, 'success');
      loadStats(college);
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Deletion failed.', 'error');
    } finally { setLoading(false); }
  };

  return (
    <div className="audit-log-data-manager-container">
      
      <div className="audit-log-data-manager-header">
        <h2 className="audit-log-data-manager-title">
          <FaDatabase className="audit-log-data-manager-icon-primary" /> Audit Log Purge Tool
        </h2>
        <p className="audit-log-data-manager-subtitle">Securely manage and delete system audit logs to free up database space.</p>
      </div>

      <div className="audit-log-data-manager-body">
        
        {/* ── STEP 1: COLLEGE ── */}
        <div className="audit-log-data-manager-step">
          <label className="audit-log-data-manager-step-label">
            <span className="audit-log-data-manager-step-num">1</span> Select College
          </label>
          <div className="audit-log-data-manager-input-wrapper">
            <FaBuilding className="audit-log-data-manager-input-icon" />
            <select className="audit-log-data-manager-select" value={college} onChange={handleCollegeChange}>
              <option value="">-- Choose a College --</option>
              {colleges.map(c => <option key={c._id} value={c._id}>{c.name} ({c.code})</option>)}
            </select>
          </div>
        </div>

        {college && statsLoading && (
          <div className="audit-log-data-manager-loading">
            <div className="audit-log-data-manager-spinner"></div> Loading college data...
          </div>
        )}

        {college && stats && !statsLoading && (
          <>
            {stats.total === 0 ? (
              <div className="audit-log-data-manager-empty-state">
                <FaExclamationCircle className="audit-log-data-manager-empty-icon" />
                <p>No audit logs exist for this college.</p>
              </div>
            ) : (
              <>
                {/* ── STEP 2: TIME RANGE ── */}
                <div className="audit-log-data-manager-step audit-log-data-manager-animate-slide">
                  <label className="audit-log-data-manager-step-label">
                    <span className="audit-log-data-manager-step-num">2</span> Select Time Range
                  </label>
                  
                  <div className="audit-log-data-manager-tabs">
                    <button className={`audit-log-data-manager-tab ${timeMode === 'all' ? 'active' : ''}`} onClick={() => setTimeMode('all')}>
                      <FaDatabase /> All Time
                    </button>
                    <button className={`audit-log-data-manager-tab ${timeMode === 'year' ? 'active' : ''}`} onClick={() => setTimeMode('year')}>
                      <FaCalendarAlt /> By Year
                    </button>
                    <button className={`audit-log-data-manager-tab ${timeMode === 'custom' ? 'active' : ''}`} onClick={() => setTimeMode('custom')}>
                      <FaClock /> Custom Date
                    </button>
                  </div>

                  <div className="audit-log-data-manager-tab-content">
                    {timeMode === 'all' && (
                      <p className="audit-log-data-manager-hint"><FaInfoCircle/> Viewing all logs ever recorded for this college.</p>
                    )}
                    
                    {timeMode === 'year' && (
                      <div className="audit-log-data-manager-input-group">
                        <label>Select Available Year</label>
                        <select className="audit-log-data-manager-select" value={selYear} onChange={e => setSelYear(e.target.value)}>
                          {stats.years.map(y => <option key={y.year} value={String(y.year)}>{y.year}</option>)}
                        </select>
                      </div>
                    )}

                    {timeMode === 'custom' && (
                      <div className="audit-log-data-manager-date-row">
                        <div className="audit-log-data-manager-input-group">
                          <label>From Date</label>
                          <input type="date" className="audit-log-data-manager-input" value={fromDate} onChange={e => setFromDate(e.target.value)} />
                        </div>
                        <div className="audit-log-data-manager-input-group">
                          <label>To Date</label>
                          <input type="date" className="audit-log-data-manager-input" value={toDate} onChange={e => setToDate(e.target.value)} />
                        </div>
                        {!currentCounts.isExact && (
                          <div className="audit-log-data-manager-warning-inline">
                            Note: Category previews below show estimated maximums for partial years. Deletion will be perfectly accurate to your dates.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── STEP 3: CATEGORY TO DELETE ── */}
                <div className="audit-log-data-manager-step audit-log-data-manager-animate-slide">
                  <label className="audit-log-data-manager-step-label">
                    <span className="audit-log-data-manager-step-num">3</span> Select Category to Purge
                  </label>
                  
                  <div className="audit-log-data-manager-category-grid">
                    {/* All Categories Card */}
                    <div 
                      className={`audit-log-data-manager-cat-card ${selCategory === '' ? 'selected' : ''}`}
                      onClick={() => setSelCategory('')}
                    >
                      <div className="audit-log-data-manager-cat-header">All Categories</div>
                      <div className="audit-log-data-manager-cat-count">{currentCounts.total} logs</div>
                    </div>

                    {/* Specific Categories */}
                    {currentCounts.categories.map(cat => (
                      <div 
                        key={cat.name}
                        className={`audit-log-data-manager-cat-card ${selCategory === cat.name ? 'selected' : ''}`}
                        onClick={() => setSelCategory(cat.name)}
                      >
                        <div className="audit-log-data-manager-cat-header">{cat.name.toUpperCase()}</div>
                        <div className="audit-log-data-manager-cat-count">{cat.count} logs</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ── ACTION AREA ── */}
                <div className="audit-log-data-manager-action-area audit-log-data-manager-animate-slide">
                  <div className="audit-log-data-manager-action-text">
                    Ready to permanently delete <strong>{finalDeleteCount}</strong> log(s).
                  </div>
                  <button 
                    className="audit-log-data-manager-btn-danger" 
                    onClick={handleDelete}
                    disabled={loading || finalDeleteCount === 0}
                  >
                    <FaTrashAlt /> {loading ? 'Purging...' : `Purge ${finalDeleteCount} Logs`}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DataManager;